import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ClickablePhone } from '../components/phone/ClickablePhone';
import {
  RefreshCw, Loader2, Link2, UserCheck, BookOpen, MessageSquare,
  Reply, CalendarCheck, Trophy, TrendingUp, Ban,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIP_STYLES = {
  crm: 'bg-teal-50 text-teal-700 border-teal-200',
  disc: 'bg-violet-50 text-violet-700 border-violet-200',
  playbook: 'bg-sky-50 text-sky-700 border-sky-200',
  sms: 'bg-amber-50 text-amber-700 border-amber-200',
  reply: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  crm_note: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  flagged: 'bg-rose-50 text-rose-700 border-rose-200',
};

const STAGE_STYLES = {
  'Closed Won': 'bg-emerald-100 text-emerald-800',
  'Call booked': 'bg-teal-100 text-teal-800',
  'Closed Lost': 'bg-slate-200 text-slate-600',
  'Written off (CRM)': 'bg-rose-100 text-rose-700',
  'Chased — no response': 'bg-orange-100 text-orange-700',
  'Replied — awaiting answer': 'bg-emerald-50 text-emerald-700',
  Contacted: 'bg-amber-100 text-amber-700',
  'Playbook ready': 'bg-sky-100 text-sky-700',
  'CRM matched': 'bg-violet-100 text-violet-700',
  New: 'bg-slate-100 text-slate-500',
};

const ago = (iso) => {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};

const MetricCard = ({ icon: Icon, label, value, sub, tone = 'text-slate-900' }) => (
  <div className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={`gs-metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className={`text-2xl font-bold mt-0.5 ${tone}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
  </div>
);

// Playbook impact — rule-based win-rate comparison (coached vs uncoached
// revivable deals) so managers can see whether playbooks lift close rates.
const PlaybookImpactCard = ({ impact }) => {
  if (!impact) return null;
  const w = impact.with_playbook || {};
  const wo = impact.without_playbook || {};
  const enough = (w.deals || 0) > 0 && (wo.deals || 0) > 0;
  const Row = ({ label, stats, barTone }) => (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-600 w-32 shrink-0 font-medium">{label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
        <div className={`h-full rounded-full ${barTone}`} style={{ width: `${Math.max(stats.win_rate_pct || 0, 2)}%` }} />
      </div>
      <span className="text-xs font-bold text-slate-800 w-14 text-right tabular-nums">{stats.win_rate_pct ?? 0}%</span>
      <span className="text-[11px] text-slate-400 w-40 shrink-0">{stats.won ?? 0} won · {stats.booked ?? 0} booked · {stats.deals ?? 0} deals</span>
    </div>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5" data-testid="gs-playbook-impact">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <BookOpen className="w-4 h-4 text-sky-600" /> Playbook impact — do coached callbacks convert?
          </h2>
          <p className="text-[10px] text-slate-400">Win rate of revivable deals WITH a generated playbook vs without. Rule-based from chase outcomes — no AI tokens.</p>
        </div>
        {enough ? (
          <span
            className={`text-xs font-bold px-3 py-1 rounded-full ${impact.lift_pts > 0 ? 'bg-emerald-100 text-emerald-700' : impact.lift_pts < 0 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}
            data-testid="gs-playbook-lift-badge"
          >
            {impact.lift_pts > 0 ? '+' : ''}{impact.lift_pts} pts win-rate {impact.lift_pts >= 0 ? 'lift' : 'gap'}
          </span>
        ) : (
          <span className="text-[11px] text-slate-400 italic">Not enough outcome data yet — mark chases won/lost to build this comparison.</span>
        )}
      </div>
      <div className="space-y-2">
        <Row label="With playbook" stats={w} barTone="bg-sky-500" />
        <Row label="Without playbook" stats={wo} barTone="bg-slate-400" />
      </div>
      {(impact.won_after_playbook ?? 0) > 0 && (
        <p className="text-[11px] text-emerald-700 mt-2.5 font-medium" data-testid="gs-won-after-playbook">
          <Trophy className="w-3 h-3 inline mr-1" />
          {impact.won_after_playbook} win{impact.won_after_playbook === 1 ? '' : 's'} landed AFTER the playbook was generated — directly playbook-guided.
        </p>
      )}
    </div>
  );
};

export default function GymSalesSyncPage() {
  const { token, user } = useAuth();
  const isMaster = user?.role === 'master_admin';
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [funnelFilter, setFunnelFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('');
  const [dealSearch, setDealSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [adminDomain, setAdminDomain] = useState('');

  useEffect(() => {
    if (!isMaster) return;
    axios.get(`${API}/admin/domains`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const list = (Array.isArray(r.data) ? r.data : []).map((d) => ({
          id: d.id, name: d.name || d.yabbit_domain || d.id,
          gymsales: !!d.gymsales_credentials?.enabled,
        }));
        list.sort((a, b) => (b.gymsales - a.gymsales) || a.name.localeCompare(b.name));
        setDomains(list);
        setAdminDomain((cur) => cur || (list.find((d) => d.gymsales) || list[0] || {}).id || '');
      }).catch(() => {});
  }, [isMaster, token]);

  const load = () => {
    if (isMaster && !adminDomain) return;
    setLoading(true);
    axios.get(`${API}/analytics/gymsales/sync-overview`, {
      params: { days, funnel: funnelFilter, site: siteFilter || undefined, domain_id: (isMaster && adminDomain) || undefined },
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => setData(r.data)).catch(() => setData({ enabled: false }))
      .finally(() => setLoading(false));
  };
  useEffect(load, [days, funnelFilter, siteFilter, token, adminDomain]); // eslint-disable-line react-hooks/exhaustive-deps

  const domainSwitcher = isMaster && domains.length > 0 && (
    <select value={adminDomain} onChange={(e) => { setSiteFilter(''); setFunnelFilter('all'); setAdminDomain(e.target.value); }}
      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 cursor-pointer"
      data-testid="gs-sync-domain-switcher">
      {domains.map((d) => (
        <option key={d.id} value={d.id}>{d.name}{d.gymsales ? '' : ' (GymSales off)'}</option>
      ))}
    </select>
  );

  if (loading && !data) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>;
  }
  if (!data?.enabled) {
    return (
      <div className="max-w-xl mx-auto mt-16 bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="gs-sync-disabled">
        <Link2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-slate-800">GymSales is not connected</h2>
        <p className="text-sm text-slate-500 mt-1">Enable GymSales credentials for this domain to see sync activity.</p>
        {domainSwitcher && <div className="mt-4 flex justify-center">{domainSwitcher}</div>}
      </div>
    );
  }

  const m = data.metrics;
  const q = dealSearch.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, '');
  const qAlt = qDigits.replace(/^0+/, '');
  const visibleDeals = !q ? data.deals : data.deals.filter((d) => {
    const name = (d.person || '').toLowerCase();
    const agent = (d.agent || '').toLowerCase();
    const phone = String(d.customer_phone || '').replace(/\D/g, '');
    return name.includes(q) || agent.includes(q)
      || (qDigits && phone.includes(qDigits))
      || (qAlt.length >= 6 && phone.includes(qAlt));
  });
  const hl = (text) => {
    const s = String(text || '');
    if (!q || !s) return s;
    let idx = s.toLowerCase().indexOf(q);
    let len = q.length;
    if (idx === -1 && qDigits) {
      idx = s.indexOf(qDigits); len = qDigits.length;
      if (idx === -1 && qAlt.length >= 6) { idx = s.indexOf(qAlt); len = qAlt.length; }
    }
    if (idx === -1) return s;
    return (<>{s.slice(0, idx)}<mark className="bg-amber-200 text-slate-900 font-bold rounded-sm px-0.5">{s.slice(idx, idx + len)}</mark>{s.slice(idx + len)}</>);
  };
  const maxFunnel = Math.max(...data.funnel.map((f) => f.count), 1);
  const rate = m.match_rate_pct;

  return (
    <div className="max-w-6xl mx-auto" data-testid="gymsales-sync-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Link2 className="w-6 h-6 text-teal-600" /> GymSales Sync
          </h1>
          <p className="text-sm text-slate-500">Matches found, intelligence attached, and how each deal is moving toward close.</p>
          {data.site_scope_applied?.length > 0 && (
            <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="gs-site-scope-note">
              Scoped to your site{data.site_scope_applied.length > 1 ? 's' : ''}: {data.site_scope_applied.join(', ')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {domainSwitcher}
          {[7, 30, 60].map((v) => (
            <button key={v} onClick={() => setDays(v)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full ${days === v ? 'bg-teal-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid={`gs-sync-days-${v}`}>{v}d</button>
          ))}
          <button onClick={load} className="p-2 rounded-full bg-white border border-slate-200 text-slate-500 hover:text-teal-600" data-testid="gs-sync-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        <MetricCard icon={UserCheck} label="Match rate" value={`${rate}%`}
          sub={`${m.matched_lookups} of ${m.lookups} lookups`}
          tone={rate >= 50 ? 'text-emerald-600' : rate > 0 ? 'text-amber-600' : 'text-slate-900'} />
        <MetricCard icon={TrendingUp} label="Revivable deals" value={m.revivable_deals} sub={`last ${data.days} days`} />
        <MetricCard icon={BookOpen} label="Playbooks" value={m.playbook} sub="coaching plans ready" />
        <MetricCard icon={MessageSquare} label="SMS sent" value={m.sms_sent} sub="follow-ups dispatched" />
        <MetricCard icon={Reply} label="Replies" value={m.replied} sub="customers engaged" tone="text-emerald-600" />
        <MetricCard icon={Trophy} label="Booked + Won" value={m.booked + m.won} sub={`${m.won} closed won`} tone="text-teal-600" />
        <MetricCard icon={Ban} label="Written off" value={m.written_off ?? 0}
          sub={`${m.written_off_bad_lead ?? 0} BADLEAD · ${m.written_off_lost ?? 0} LOST`}
          tone={(m.written_off ?? 0) > 0 ? 'text-rose-600' : 'text-slate-900'} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        <div className="bg-white border border-slate-200 rounded-xl p-4 lg:col-span-1" data-testid="gs-sync-funnel">
          <h2 className="text-sm font-semibold text-slate-800 mb-0.5">Deal journey funnel</h2>
          <p className="text-[10px] text-slate-400 mb-3">Tap a stage to see which deals are sitting there.</p>
          <div className="space-y-2">
            {data.funnel.map((f) => (
              <button key={f.key} onClick={() => setFunnelFilter(funnelFilter === f.key ? 'all' : f.key)}
                className={`w-full text-left rounded-lg px-2 py-1 -mx-2 transition-colors ${funnelFilter === f.key ? 'bg-teal-50 ring-1 ring-teal-300' : 'hover:bg-slate-50'}`}
                data-testid={`gs-funnel-stage-${f.key}`}>
                <div className="flex justify-between text-xs mb-0.5">
                  <span className={`font-medium ${funnelFilter === f.key ? 'text-teal-700' : 'text-slate-600'}`}>{f.label}</span>
                  <span className="text-slate-400">{f.count}</span>
                </div>
                <div className="bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-teal-500" style={{ width: `${Math.max((f.count / maxFunnel) * 100, 2)}%` }} />
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 lg:col-span-2" data-testid="gs-sync-trend">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Daily match rate</h2>
          {data.trend.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No lookups in this window yet.</p>
          ) : (
            <div className="flex items-end gap-1 h-28">
              {data.trend.map((t) => (
                <div key={t.date} className="flex-1 flex flex-col items-center gap-1" title={`${t.date}: ${t.matched}/${t.total} (${t.rate}%)`}>
                  <div className="w-full bg-slate-100 rounded relative" style={{ height: '90px' }}>
                    <div className={`absolute bottom-0 w-full rounded ${t.rate >= 50 ? 'bg-teal-500' : t.rate > 0 ? 'bg-amber-400' : 'bg-rose-300'}`}
                      style={{ height: `${Math.max(t.rate, 3)}%` }} />
                  </div>
                  <span className="text-[9px] text-slate-400">{t.date.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <PlaybookImpactCard impact={data.playbook_impact} />

      {/* Per-site rollups — multi-site tenants (Chermside / Morningside) */}
      {data.per_site?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="gs-sync-per-site">
          <h2 className="text-sm font-semibold text-slate-800 mb-0.5">Site leaderboard — revival win rate</h2>
          <p className="text-[11px] text-slate-400 mb-3">Clubs ranked by revival win rate. Click a club to filter the funnel and deal list.</p>
          {data.leaderboard_insight && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="gs-leaderboard-insight">
              <span className="text-base leading-none mt-0.5">💡</span>
              <p className="text-xs text-amber-900">
                <span className="font-bold uppercase tracking-wide text-[10px] text-amber-700">What #1 does differently: </span>
                {data.leaderboard_insight}
              </p>
            </div>
          )}
          {siteFilter && (
            <button onClick={() => setSiteFilter('')}
              className="mb-2 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
              data-testid="gs-site-clear-filter">
              Showing: {siteFilter} ✕
            </button>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.per_site.map((s, idx) => (
              <button
                type="button"
                key={s.site}
                onClick={() => setSiteFilter(siteFilter === s.site ? '' : s.site)}
                className={`border rounded-lg p-3 text-left transition-colors ${siteFilter === s.site ? 'border-emerald-400 ring-1 ring-emerald-300 bg-emerald-50/40' : 'border-slate-100 hover:border-emerald-200 hover:bg-slate-50'}`}
                data-testid={`gs-site-roll-${s.site}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
                    <span className="text-slate-400 text-xs">#{idx + 1}</span>
                    {s.site}
                    {idx === 0 && (s.win_rate || 0) > 0 && data.per_site.length > 1 && (
                      <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" data-testid="gs-site-top-badge">🏆 strongest</span>
                    )}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    <span className="font-bold text-emerald-700">{s.win_rate ?? 0}%</span> win · {s.deals} deal{s.deals === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5 text-center">
                  {[['Playbook', s.playbook, 'text-indigo-700 bg-indigo-50'],
                    ['Contacted', s.contacted, 'text-sky-700 bg-sky-50'],
                    ['Replied', s.replied, 'text-teal-700 bg-teal-50'],
                    ['Booked/Won', s.booked_won, 'text-emerald-700 bg-emerald-50'],
                    ['Won', s.won, 'text-emerald-800 bg-emerald-100'],
                    ['Written off', s.written_off, 'text-rose-700 bg-rose-50']].map(([label, val, klass]) => (
                    <div key={label} className={`rounded-md px-1 py-1.5 ${klass}`}>
                      <div className="text-base font-bold leading-none">{val}</div>
                      <div className="text-[9px] uppercase tracking-wider mt-0.5 opacity-80">{label}</div>
                    </div>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="gs-sync-deals">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Deals with intelligence attached</h2>
            <p className="text-[11px] text-slate-400">Stage and next step are rule-based (no AI tokens spent on this screen).</p>
          </div>
          <input
            type="search"
            value={dealSearch}
            onChange={(e) => setDealSearch(e.target.value)}
            placeholder="Search phone or name…"
            className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-teal-400 w-48"
            data-testid="gs-sync-deal-search"
          />
          {funnelFilter !== 'all' && (
            <button onClick={() => setFunnelFilter('all')}
              className="text-xs font-semibold px-3 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100"
              data-testid="gs-funnel-clear-filter">
              Filter: {data.funnel.find((x) => x.key === funnelFilter)?.label} ✕
            </button>
          )}
        </div>
        {visibleDeals.length === 0 ? (
          <p className="text-sm text-slate-400 italic px-4 py-6" data-testid="gs-sync-deals-empty">
          {q ? `No deals match “${dealSearch}” — try fewer digits or a different spelling.`
            : funnelFilter === 'all' ? 'No revivable deals in this window.' : 'No deals sitting in this stage — tap the stage again to clear the filter.'}
        </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {visibleDeals.map((d) => (
              <div key={d.call_id} className="px-4 py-3 hover:bg-slate-50" data-testid={`gs-sync-deal-${d.call_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800 text-sm">{hl(d.person || d.customer_phone) || 'Unknown caller'}</span>
                      {d.customer_phone && (
                        <ClickablePhone number={d.customer_phone} iconOnly
                          meta={{ reason: 'gymsales_deal', context: { call_id: d.call_id } }} />
                      )}
                      {(d.site || d.ext_site) && (
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${d.site ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}
                          title={d.site ? 'Club from the GymSales CRM match' : 'Club inferred from the answering extension'}
                          data-testid={`gs-sync-site-${d.call_id}`}>
                          {d.site || d.ext_site}
                        </span>
                      )}
                      {d.extension && (
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200" data-testid={`gs-sync-ext-${d.call_id}`}>Ext {d.extension}</span>
                      )}
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STAGE_STYLES[d.stage] || 'bg-slate-100 text-slate-500'}`}>{d.stage}</span>
                      {d.confidence != null && <span className="text-[11px] text-slate-400">{d.confidence}% confidence</span>}
                    </div>
                    {d.reason && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{d.reason}</p>}
                    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                      {d.intel.length === 0 ? (
                        <span className="text-[11px] text-slate-400 italic">No intelligence attached yet</span>
                      ) : d.intel.map((c) => (
                        <span key={c.key} className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${CHIP_STYLES[c.key] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{c.label}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[11px] text-slate-400">{d.agent ? `Agent: ${d.agent}` : ''} · {ago(d.created_at)}</div>
                    {d.crm_url ? (
                      <a href={d.crm_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs font-medium text-teal-700 mt-1 flex items-center gap-1 justify-end hover:text-teal-900 hover:underline"
                        data-testid={`gs-sync-next-step-${d.call_id}`}>
                        <CalendarCheck className="w-3.5 h-3.5" /> {d.next_step} ↗
                      </a>
                    ) : (
                      <a href={`/calls/${d.call_id}`}
                        className="text-xs font-medium text-teal-700 mt-1 flex items-center gap-1 justify-end hover:text-teal-900 hover:underline"
                        data-testid={`gs-sync-next-step-${d.call_id}`}>
                        <CalendarCheck className="w-3.5 h-3.5" /> {d.next_step}
                      </a>
                    )}
                    <a href={`/calls/${d.call_id}`} className="text-[11px] text-slate-400 hover:text-teal-600 underline" data-testid={`gs-sync-open-${d.call_id}`}>Open call</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
